import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useContext, useMemo, useState } from "react";

type ListHook<T> = (arg: { isDefault: boolean }) => {
  data?: { data: T[] };
  isLoading?: boolean;
};

type CopyHook<T> = () => [
  (arg: { id: string; body: { providerId: string; payerId: number } }) => {
    unwrap: () => Promise<T>;
  },
  { isLoading: boolean }
];

type AddFromDefaultButtonProps<
  T extends { id: string; name?: string; groupName?: string }
> = {
  label?: string;
  providerId: string;
  payerId: number;
  entityLabel: string;
  /** Hook that fetches the list; should accept `{ isDefault: true }`. */
  useList: ListHook<T>;
  /** Mutation hook for copy-to-contract. */
  useCopy: CopyHook<unknown>;
  onSuccess?: () => void;
  /** Controlled open state; when provided the dialog is driven externally. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (e.g. when opened from another control). */
  hideTrigger?: boolean;
};

function AddFromDefaultButton<
  T extends { id: string; name?: string; groupName?: string }
>({
  label = "Add from default",
  providerId,
  payerId,
  entityLabel,
  useList,
  useCopy,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: AddFromDefaultButtonProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { current: toast } = useContext(ToastContext);
  const { data, isLoading } = useList({ isDefault: true });
  const [copy, { isLoading: isCopying }] = useCopy();

  const options = useMemo(() => {
    const rows = data?.data ?? [];
    return rows.map((r) => ({
      label: r.name || r.groupName || r.id.slice(0, 8),
      value: r.id,
    }));
  }, [data]);

  const onHide = () => {
    setOpen(false);
    setSelectedId(null);
  };

  const onConfirm = async () => {
    if (!selectedId) return;
    try {
      await copy({
        id: selectedId,
        body: { providerId, payerId },
      }).unwrap();
      toast?.show({
        severity: "success",
        summary: "Added",
        detail: `${entityLabel} copied from default.`,
      });
      onSuccess?.();
      onHide();
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = e as any;
      const data = err?.data;
      let message: string | null = null;
      if (typeof data === "string") message = data;
      else if (data && typeof data === "object") {
        if (typeof data.message === "string") message = data.message;
        else if (typeof data.error === "string") message = data.error;
      }
      toast?.show({
        severity: "error",
        summary: "Error",
        detail:
          err?.status === 400 && message
            ? message
            : "Unable to copy from default.",
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          label={label}
          icon="pi pi-copy"
          className="p-button-text p-button-sm"
          onClick={() => setOpen(true)}
        />
      )}
      <Dialog
        header={`${label} — ${entityLabel}`}
        visible={open}
        onHide={onHide}
        style={{ width: "420px", maxWidth: "95vw" }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={onHide}
              disabled={isCopying}
            />
            <Button
              label="Add"
              icon="pi pi-check"
              onClick={onConfirm}
              disabled={!selectedId || isCopying}
              loading={isCopying}
            />
          </div>
        }
      >
        <p style={{ fontSize: 13, color: "#4b5563", marginTop: 0 }}>
          Pick a global default rule to copy into this contract. A new copy
          scoped to this provider and payer will be created.
        </p>
        <Dropdown
          value={selectedId}
          options={options}
          onChange={(e) => setSelectedId(e.value)}
          placeholder={isLoading ? "Loading…" : "Select a default"}
          filter
          filterBy="label"
          style={{ width: "100%" }}
          emptyMessage="No defaults found"
        />
      </Dialog>
    </>
  );
}

export default AddFromDefaultButton;
